import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, InputNumber, notification } from 'antd';
import { httpPost, httpGet, customBaseUrl } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';

const { Option } = Select;



const AdminBillsPayment = ({ userId, userWallets, onPaymentSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedBillType, setSelectedBillType] = useState(null);
  const [validatingAccount, setValidatingAccount] = useState(false);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [loadingServiceType, setLoadingServiceType] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [bundleOptions, setBundleOptions] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);

  useEffect(() => {
    if (!selectedBillType) {
      setServiceTypeOptions([]);
      return;
    }

    const fetchProviders = async () => {
      setLoadingServiceType(true);
      form.resetFields(['serviceType']);
      let url = '';
      let dataExtractor = (res) => res.data?.data?.providers || [];

      switch (selectedBillType) {
        case 'AIRTIME':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/airtime/providers`;
          break;
        case 'DATA':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/data/providers`;
          break;
        case 'CABLE':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/cable/providers`;
          break;
        case 'ELECTRICITY':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/electricity/providers`;
          dataExtractor = (res) => res.data?.data || [];
          break;
        case 'EPIN':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/epin/providers`;
          break;
        case 'BETTING':
          setServiceTypeOptions(['bet9ja', 'nairabet', 'sportybet'].map(st => ({ name: st.toUpperCase(), service_type: st })));
          setLoadingServiceType(false);
          return;
        default:
          setServiceTypeOptions([]);
          setLoadingServiceType(false);
          return;
      }

      try {
        const res = await httpGet(url);
        if (res.success || res.status) {
          const providers = dataExtractor(res);
          const options = providers.map(p => ({ name: p.name, service_type: p.service_type })).filter(p => p.service_type);
          const uniqueOptions = Array.from(new Map(options.map(item => [item.service_type, item])).values());
          setServiceTypeOptions(uniqueOptions);
        } else {
          notification.error({ message: `Failed to fetch ${selectedBillType} providers.`, description: res.message });
          setServiceTypeOptions([]);
        }
      } catch (error) {
        notification.error({ message: `Error fetching ${selectedBillType} providers.`, description: error.message });
        setServiceTypeOptions([]);
      } finally {
        setLoadingServiceType(false);
      }
    };

    fetchProviders();
  }, [selectedBillType, form]);

  useEffect(() => {
    if (!selectedBillType || !selectedServiceType || !['EPIN', 'DATA', 'CABLE'].includes(selectedBillType)) {
      setBundleOptions([]);
      return;
    }

    const fetchBundles = async () => {
      setLoadingBundles(true);
      setBundleOptions([]);
      setSelectedBundle(null);
      form.resetFields(['billPayload']);

      let url = '';
      let dataExtractor = (res) => res.data?.data || [];

      switch (selectedBillType) {
        case 'EPIN':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/epin/bundles?serviceType=${selectedServiceType}`;
          break;
        case 'DATA':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/data/bundles?serviceType=${selectedServiceType}`;
          break;
        case 'CABLE':
          url = `${customBaseUrl.tranv2}/api/v1/wayabills/cable/bouquets?serviceType=${selectedServiceType}`;
          break;
        default:
          setLoadingBundles(false);
          return;
      }

      try {
        const res = await httpGet(url);
        if (res.success || res.status) {
          const bundles = dataExtractor(res);
          let options = [];
          if (selectedBillType === 'EPIN') {
            options = bundles.map(b => ({ name: b.description, value: b.amount, ...b })).filter(b => b.value !== null && b.value !== undefined);
          } else if (selectedBillType === 'DATA') {
            options = bundles.map(b => ({ name: `${b.name} - ${b.price}`, value: b.datacode, ...b })).filter(b => b.value !== null && b.value !== undefined);
          } else if (selectedBillType === 'CABLE') {
            options = bundles.map(b => ({ name: b.name, value: b.code, ...b })).filter(b => b.value !== null && b.value !== undefined);
          }
          setBundleOptions(options);
        } else {
          notification.error({ message: `Failed to fetch ${selectedBillType} bundles.`, description: res.message });
        }
      } catch (error) {
        notification.error({ message: `Error fetching ${selectedBillType} bundles.`, description: error.message });
      } finally {
        setLoadingBundles(false);
      }
    };

    fetchBundles();
  }, [selectedBillType, selectedServiceType, form]);

  const handleBillTypeChange = (value) => {
    setSelectedBillType(value);
    setSelectedServiceType(null); // Reset selected service type
    setBundleOptions([]); // Clear bundle options
    setSelectedBundle(null); // Clear selected bundle
    form.resetFields(['serviceType', 'phoneNumber', 'smartcardNo', 'meterNo', 'billPayload', 'customerName']);
  };

  const handleValidate = async () => {
    const service_type = form.getFieldValue('serviceType');
    if (!service_type) {
      notification.error({ message: 'Please select a service type first.' });
      return;
    }

    let account_number;
    switch (selectedBillType) {
      case 'CABLE':
        account_number = form.getFieldValue('smartcardNo');
        break;
      case 'ELECTRICITY':
        account_number = form.getFieldValue('meterNo');
        break;
      case 'BETTING':
        account_number = form.getFieldValue('phoneNumber');
        break;
      default:
        return;
    }

    if (!account_number) {
      notification.error({ message: 'Please enter the account/meter/phone number to validate.' });
      return;
    }

    setValidatingAccount(true);
    try {
      const payload = { service_type, account_number };
      const res = await httpPost(`${customBaseUrl.tranv2}/api/v1/wayabills/validate`, payload);

      if (res.success || res.status === true) {
        const customerName = res.data?.data?.user?.name;
        if (customerName) {
          form.setFieldsValue({ customerName: customerName });
          notification.success({ message: 'Account validated successfully!' });
        } else {
          notification.warning({ message: 'Validation successful, but no customer name was returned.' });
        }
      } else {
        notification.error({ message: res.message || 'Failed to validate account.' });
      }
    } catch (error) {
      notification.error({ message: error.message || 'An error occurred during validation.' });
    } finally {
      setValidatingAccount(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    showLoader();

    let billPayloadObject = {};
    if (['AIRTIME', 'BETTING'].includes(values.billType)) { // DATA and EPIN now have bundles
      billPayloadObject.phone = values.phoneNumber;
    } else if (values.billType === 'DATA') {
      billPayloadObject.phone = values.phoneNumber;
      if (selectedBundle) {
        billPayloadObject.datacode = selectedBundle.datacode;
        billPayloadObject.bundleName = selectedBundle.name;
        billPayloadObject.bundlePrice = selectedBundle.price;
      }
    } else if (values.billType === 'EPIN') {
      billPayloadObject.phone = values.phoneNumber;
      if (selectedBundle) {
        billPayloadObject.epinAmount = selectedBundle.amount;
        billPayloadObject.epinDescription = selectedBundle.description;
      }
    } else if (values.billType === 'CABLE') {
      billPayloadObject.smartcardNo = values.smartcardNo;
      if (selectedBundle) {
        billPayloadObject.bouquetCode = selectedBundle.code;
        billPayloadObject.bouquetName = selectedBundle.name;
        // Assuming amount is still manually entered, or taken from selectedBundle.availablePricingOptions[0].price if needed
      }
    } else if (values.billType === 'ELECTRICITY') {
      billPayloadObject.meter = values.meterNo;
    } else {
      try {
        if (values.billPayload) {
          billPayloadObject = JSON.parse(values.billPayload);
        }
      } catch (e) {
        notification.error({
          message: 'Invalid Bill Payload',
          description: 'Bill Payload must be a valid JSON string.',
        });
        setLoading(false);
        hideLoader();
        return;
      }
    }

    const payload = {
      userId: userId,
      billType: values.billType,
      amount: Number(values.amount),
      accountNumber: values.accountNumber,
      serviceType: values.serviceType,
      billPayload: JSON.stringify(billPayloadObject),
      customerName: values.customerName,
      details: values.details,
      makerReason: values.makerReason,
      expiryHours: Number(values.expiryHours) || 24,
    };

    try {
      const res = await httpPost(
        `${customBaseUrl.tranv2}/api/v1/admin/maker-checker/maker/bills-payment`,
        payload
      );
      if (res.success || res.status === true) {
        notification.success({
          message: 'Bill Payment Successful',
          description: res.message || 'The bill payment has been successfully made for the user.',
        });
        form.resetFields();
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
      } else {
        notification.error({
          message: 'Bill Payment Failed',
          description: res.message || 'An error occurred while making the bill payment.',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Bill Payment Failed',
        description: error.message || 'An unexpected error occurred.',
      });
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ expiryHours: 24 }}
    >
      <Form.Item
        name="billType"
        label="Bill Type"
        rules={[{ required: true, message: 'Please select a bill type' }]}
      >
        <Select placeholder="Select bill type" onChange={handleBillTypeChange}>
          <Option value="AIRTIME">AIRTIME</Option>
          <Option value="DATA">DATA</Option>
          <Option value="CABLE">CABLE</Option>
          <Option value="ELECTRICITY">ELECTRICITY</Option>
          <Option value="EPIN">EPIN</Option>
          <Option value="BETTING">BETTING</Option>
        </Select>
      </Form.Item>

      {selectedBillType && (
        <Form.Item
          name="serviceType"
          label="Service Type"
          rules={[{ required: true, message: 'Please select a service type' }]}
        >
          <Select
            placeholder="Select service type"
            loading={loadingServiceType}
            onChange={(value) => {
              setSelectedServiceType(value);
              setSelectedBundle(null); // Reset selected bundle when service type changes
              form.resetFields(['billPayload']); // Reset billPayload if it depends on bundle
            }}
          >
            {serviceTypeOptions.map(service => (
              <Option key={service.service_type} value={service.service_type}>{service.name}</Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {selectedBillType && selectedServiceType && ['EPIN', 'DATA', 'CABLE'].includes(selectedBillType) && (
        <Form.Item
          name="bundle"
          label={selectedBillType === 'CABLE' ? 'Bouquet' : 'Bundle'}
          rules={[{ required: true, message: `Please select a ${selectedBillType === 'CABLE' ? 'bouquet' : 'bundle'}` }]}
        >
          <Select
            placeholder={`Select ${selectedBillType === 'CABLE' ? 'bouquet' : 'bundle'}`}
            loading={loadingBundles}
            onChange={(value) => {
              const bundle = bundleOptions.find(b => b.value === value);
              setSelectedBundle(bundle);
              // Optionally set amount if bundle has a price
              if (bundle && selectedBillType === 'CABLE' && bundle.availablePricingOptions && bundle.availablePricingOptions.length > 0) {
                form.setFieldsValue({ amount: bundle.availablePricingOptions[0].price });
              } else if (bundle && bundle.price) {
                form.setFieldsValue({ amount: bundle.price });
              } else if (bundle && bundle.amount) { // For EPIN
                form.setFieldsValue({ amount: bundle.amount });
              }
            }}
          >
            {bundleOptions.map(bundle => (
              <Option key={bundle.value} value={bundle.value}>
                {bundle.name}
                {bundle.price && ` - ${bundle.price}`}
                {bundle.amount && ` - ${bundle.amount}`}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        name="amount"
        label="Amount"
        rules={[{ required: true, message: 'Please enter the amount' }]}
      >
        <InputNumber style={{ width: '100%' }} placeholder="Amount" />
      </Form.Item>

      <Form.Item
        name="accountNumber"
        label="Source Account Number"
        rules={[{ required: true, message: 'Please select the source account' }]}
      >
        <Select placeholder="Select source account">
          {userWallets?.map(wallet => (
            <Option key={wallet.accountNumber} value={wallet.accountNumber}>
              {wallet.accountName} ({wallet.accountNumber})
            </Option>
          ))}
        </Select>
      </Form.Item>

      {['AIRTIME', 'DATA', 'EPIN'].includes(selectedBillType) && (
        <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true }]}>
          <Input placeholder="Phone Number" />
        </Form.Item>
      )}

      {selectedBillType === 'BETTING' && (
        <Form.Item label="Phone Number">
          <Input.Group compact>
            <Form.Item name="phoneNumber" noStyle rules={[{ required: true }]}>
              <Input style={{ width: 'calc(100% - 100px)' }} placeholder="Phone Number" />
            </Form.Item>
            <Button style={{ width: '100px' }} onClick={handleValidate} loading={validatingAccount}>
              Verify
            </Button>
          </Input.Group>
        </Form.Item>
      )}

      {selectedBillType === 'CABLE' && (
        <Form.Item label="Smartcard Number">
          <Input.Group compact>
            <Form.Item name="smartcardNo" noStyle rules={[{ required: true }]}>
              <Input style={{ width: 'calc(100% - 100px)' }} placeholder="Smartcard Number" />
            </Form.Item>
            <Button style={{ width: '100px' }} onClick={handleValidate} loading={validatingAccount}>
              Verify
            </Button>
          </Input.Group>
        </Form.Item>
      )}

      {selectedBillType === 'ELECTRICITY' && (
        <Form.Item label="Meter Number">
          <Input.Group compact>
            <Form.Item name="meterNo" noStyle rules={[{ required: true }]}>
              <Input style={{ width: 'calc(100% - 100px)' }} placeholder="Meter Number" />
            </Form.Item>
            <Button style={{ width: '100px' }} onClick={handleValidate} loading={validatingAccount}>
              Verify
            </Button>
          </Input.Group>
        </Form.Item>
      )}

      {!['AIRTIME', 'DATA', 'CABLE', 'ELECTRICITY', 'EPIN', 'BETTING'].includes(selectedBillType) && selectedBillType && (
        <Form.Item
          name="billPayload"
          label="Bill Payload (JSON)"
          rules={[{ required: true, message: 'Please enter the bill payload' }]}
        >
          <Input.TextArea rows={3} placeholder='e.g., {"some_key":"some_value"}' />
        </Form.Item>
      )}

      <Form.Item
        name="customerName"
        label="Customer Name"
        rules={[{ required: true }]}
      >
        <Input placeholder="Customer Name (auto-filled on validation)" />
      </Form.Item>

      <Form.Item
        name="details"
        label="Details"
      >
        <Input.TextArea rows={2} placeholder="Details about the bill payment" />
      </Form.Item>

      <Form.Item
        name="makerReason"
        label="Reason for Payment"
        rules={[{ required: true, message: 'Please provide a reason for this payment' }]}
      >
        <Input.TextArea rows={2} placeholder="Reason for creating this transaction" />
      </Form.Item>

      <Form.Item
        name="expiryHours"
        label="Expiry Hours"
      >
        <InputNumber style={{ width: '100%' }} placeholder="e.g., 24" />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Make Payment
        </Button>
      </Form.Item>
    </Form>
  );
};

export default AdminBillsPayment;